import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { serviceSchema } from "../components/seoSchemas";
import { getServiceBySlug, getServiceSidebarVisual, getServiceVisual, services, site } from "../data/site";

export default function ServicePage() {
  const { slug = "" } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return (
      <>
        <SEO title="Service Not Found | Polishing Singapore" description="The requested polishing service page could not be found." canonical={`${site.domain}/services`} noindex />
        <PageHero title="Service page not found" description="This service page is not available. Browse the main services index instead." />
        <section className="section-pad">
          <div className="section-shell">
            <Link to="/services" className="btn-primary">View all services</Link>
          </div>
        </section>
      </>
    );
  }

  const related = services.filter((item) => item.category === service.category && item.slug !== service.slug).slice(0, 5);
  const serviceVisual = getServiceVisual(service);
  const sidebarVisual = getServiceSidebarVisual(service);

  return (
    <>
      <SEO
        title={`${service.title} Singapore | Polishing Singapore`}
        description={service.excerpt}
        canonical={`${site.domain}/services/${service.slug}`}
        image={serviceVisual}
        jsonLd={serviceSchema(service.title, service.excerpt, `/services/${service.slug}`)}
      />
      <PageHero title={`${service.title} Singapore`} description={service.excerpt} image={serviceVisual} />

      <section className="section-pad bg-background-50">
        <div className="section-shell grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
          <article className="surface-card p-6 md:p-10">
            <p className="eyebrow">{service.category}</p>
            <h2 className="mt-3 font-heading text-4xl font-bold leading-tight">About {service.title}</h2>
            <div className="prose-polishing mt-6">
              {service.paragraphs.map((paragraph, index) => (
                <p key={`${service.slug}-${index}`}>{paragraph}</p>
              ))}
            </div>
          </article>

          <aside className="space-y-6">
            <div className="surface-card overflow-hidden">
              <img src={sidebarVisual} alt={service.title} className="h-64 w-full object-cover" />
              <div className="p-6">
                <h2 className="font-heading text-2xl font-bold">Need this surface checked?</h2>
                <p className="mt-3 text-sm leading-7 text-foreground-700">
                  Send photos or request a site assessment before confirming polishing, repair or restoration scope.
                </p>
                <a href={site.whatsappUrl} className="mt-5 btn-primary w-full">
                  WhatsApp for Assessment
                  <ArrowRight size={17} />
                </a>
                <a href={`mailto:${site.email}?subject=${encodeURIComponent(service.title)}`} className="mt-3 inline-flex w-full items-center justify-center gap-2 text-sm font-bold text-primary-700 hover:text-primary-900">
                  Or enquire by email
                  <ArrowRight size={15} />
                </a>
              </div>
            </div>

            <div className="surface-card p-6">
              <h2 className="font-heading text-2xl font-bold">Related {service.category} pages</h2>
              <div className="mt-4 grid gap-3">
                {related.map((item) => (
                  <Link key={item.slug} to={`/services/${item.slug}`} className="flex items-start gap-3 rounded-md border border-background-200 p-3 text-sm font-semibold text-foreground-800 hover:border-primary-300 hover:text-primary-700">
                    <CheckCircle2 className="mt-0.5 shrink-0 text-primary-600" size={16} />
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
